'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { MapPin, Mail, Phone, ExternalLink } from 'lucide-react';

interface FooterProps {
  language: 'en' | 'ar';
  onQROpen: (type: 'whatsapp' | 'facebook') => void;
}

export default function Footer({ language, onQROpen }: FooterProps) {
  const [hoveredQR, setHoveredQR] = useState<string | null>(null);
  const [year, setYear] = useState(2025);

  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);

  const quickLinks = [
    { labelEn: 'Shop', labelAr: 'المتجر', href: '#products' },
    { labelEn: 'Services', labelAr: 'الخدمات', href: '#services' },
    { labelEn: 'About Us', labelAr: 'عن الشركة', href: '#about' },
    { labelEn: 'Track Order', labelAr: 'تتبع طلبيةك', href: '#track' },
    { labelEn: 'Warranty Policy', labelAr: 'سياسة الضمان', href: '#warranty' },
  ];

  return (
    <footer className="bg-onyx text-white" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Column 1: Brand */}
          <div>
            <div className="mb-5">
              <Image
                src="/assets/images/PLC-Iraq_Logo-1788026633299.jpg"
                alt="PLC-IRAQ Logo"
                width={120}
                height={48}
                className="h-12 w-auto object-contain brightness-0 invert"
              />
            </div>
            <p className="text-slate-400 text-sm font-roboto leading-relaxed mb-5">
              {language === 'ar' ?'ندعم مستقبل العراق الصناعي. شريكك في الأتمتة الصناعية على أرض العراق.' : "Powering Iraq's Industrial Future. Your automation partner, on the ground."}
            </p>
            <div className="flex flex-col gap-1.5">
              {['#PoweringIraqsFuture', '#ZeroDowntime', '#AutomationPartnerIQ'].map((tag) => (
                <span key={tag} className="text-xs text-iraqi-red font-roboto opacity-80">{tag}</span>
              ))}
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-sm font-semibold text-white font-space-grotesk uppercase tracking-wider mb-5">
              {language === 'ar' ? 'روابط سريعة' : 'Quick Links'}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-sm text-slate-400 hover:text-white transition-colors duration-200 font-roboto flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-iraqi-red opacity-50 group-hover:opacity-100 transition-opacity duration-200" />
                    {language === 'ar' ? link.labelAr : link.labelEn}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Contact */}
          <div>
            <h4 className="text-sm font-semibold text-white font-space-grotesk uppercase tracking-wider mb-5">
              {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-iraqi-red mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm text-slate-300 font-roboto">Basrah, Iraq</div>
                  <div className="text-sm text-slate-400 font-cairo">البصرة، العراق</div>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-iraqi-red flex-shrink-0" />
                <a
                  href="mailto:info@plciraq.com"
                  className="text-sm text-slate-400 hover:text-white transition-colors duration-200 font-roboto"
                >
                  info@plciraq.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-iraqi-red flex-shrink-0" />
                <a
                  href="tel:+9647731885522"
                  className="text-sm text-slate-400 hover:text-white transition-colors duration-200 font-roboto"
                >
                  +964 773 188 5522
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/9647731885522"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 text-iraqi-green text-xs font-semibold px-4 py-2 rounded-lg transition-all duration-200 border border-iraqi-green"
                  style={{ backgroundColor: 'rgba(0, 122, 61, 0.15)' }}
                >
                  <ExternalLink size={12} />
                  {language === 'ar' ? 'تواصل عبر واتسآب' : 'Chat on WhatsApp'}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: QR Codes */}
          <div>
            <h4 className="text-sm font-semibold text-white font-space-grotesk uppercase tracking-wider mb-2">
              {language === 'ar' ? 'امسح للتواصل فوراً' : 'Scan to Connect Instantly'}
            </h4>
            <p className="text-xs text-slate-500 font-roboto mb-5">
              {language === 'ar' ? 'امسح للدردشة عبر واتسآب أو متابعتنا على فيسبوك' : 'Scan to chat on WhatsApp or follow our latest updates'}
            </p>
            <div className="flex gap-4">
              {/* WhatsApp QR */}
              <button
                onClick={() => onQROpen('whatsapp')}
                onMouseEnter={() => setHoveredQR('whatsapp')}
                onMouseLeave={() => setHoveredQR(null)}
                className={`flex flex-col items-center gap-2 p-3 border rounded-xl transition-all duration-300 cursor-pointer ${
                  hoveredQR === 'whatsapp' ?'border-iraqi-green shadow-lg bg-white/5' :'border-white/20 hover:border-iraqi-green'
                }`}
                aria-label="Click to enlarge WhatsApp QR code for scanning"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onQROpen('whatsapp'); }}
              >
                <div className="w-20 h-20 bg-white rounded-lg overflow-hidden">
                  <img
                    src="/assets/images/WhatsApp_QR_Code-1788029252557.jpeg"
                    alt="Scan to chat with PLC-IRAQ on WhatsApp"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <span className="text-xs text-slate-300 font-roboto">WhatsApp</span>
              </button>

              {/* Facebook QR */}
              <button
                onClick={() => onQROpen('facebook')}
                onMouseEnter={() => setHoveredQR('facebook')}
                onMouseLeave={() => setHoveredQR(null)}
                className={`flex flex-col items-center gap-2 p-3 border rounded-xl transition-all duration-300 cursor-pointer ${
                  hoveredQR === 'facebook' ?'border-blue-400 shadow-lg bg-white/5' :'border-white/20 hover:border-blue-400'
                }`}
                aria-label="Click to enlarge Facebook QR code for scanning"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onQROpen('facebook'); }}
              >
                <div className="w-20 h-20 bg-white rounded-lg overflow-hidden">
                  <img
                    src="/assets/images/Facebook_QR_Code-1788029252586.jpeg"
                    alt="Follow PLC-IRAQ on Facebook"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <span className="text-xs text-slate-300 font-roboto">Facebook</span>
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500 font-roboto">
            &copy; {year} PLC-IRAQ. {language === 'ar' ? 'جميع الحقوق محفوظة.' : 'All rights reserved.'}
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500 font-roboto">
              {language === 'ar' ? 'شريك معتمد لسيمنز وABB وشنايدر' : 'Authorised Partner: Siemens | ABB | Schneider Electric'}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
